import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Footer() {
  const { t } = useTranslation();

  const navigation = {
    shop: [
      { name: t("footer.allProducts"), href: "/products-filters" },
      { name: t("footer.categories"), href: "/all-categories" },
      { name: t("footer.styleGuide"), href: "/style-guide" },
      { name: t("footer.courses"), href: "/courses" },
    ],
    support: [
      { name: t("footer.contact"), href: "mailto:hola@mexabeer.com" },
      { name: t("footer.privacyPolicy"), href: "/privacy-policy" },
      { name: t("footer.securityPolicy"), href: "/security-policy" },
      { name: t("footer.terms"), href: "/terms-of-service" },
    ],
    social: [
      {
        name: "Instagram",
        href: "https://www.instagram.com/mexabeer/",
        icon: (props) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
          </svg>
        ),
      },
    ],
  };

  return (
    <footer className="bg-black" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">
        Footer
      </h2>
      <div className="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8 lg:pt-32">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          {/* Brand info */}
          <div className="space-y-8">
            <Link to="/" className="-m-1.5 p-1.5 flex items-center space-x-2">
              <span className="text-2xl font-bold text-amber-500">MexaBeer</span>
            </Link>
            <p className="text-sm leading-6 text-gray-300">
              {t("footer.tagline", "Cerveza artesanal mexicana, directo a tu puerta. Descubre sabores únicos y apoya a productores locales.")}
            </p>
            <div className="flex space-x-6">
              {navigation.social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-gray-400"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-6 w-6" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
          {/* Links */}
          <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold leading-6 text-white">
                  {t("footer.shop")}
                </h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.shop.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className="text-sm leading-6 text-gray-300 hover:text-white"
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-10 md:mt-0">
                <h3 className="text-sm font-semibold leading-6 text-white">
                  {t("footer.support")}
                </h3>
                <ul role="list" className="mt-6 space-y-4">
                  {navigation.support.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className="text-sm leading-6 text-gray-300 hover:text-white"
                      >
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {/* Newsletter placeholder (optional) */}
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div className="mt-10 md:mt-0">
                <h3 className="text-sm font-semibold leading-6 text-white">
                  {t("footer.newsletter")}
                </h3>
                <p className="mt-2 text-sm leading-6 text-gray-300">
                  {t("footer.newsletterText", "Recibe ofertas exclusivas y nuevos lanzamientos.")}
                </p>
                <form className="mt-4 flex flex-col gap-3 max-w-md sm:max-w-lg">
                  <label htmlFor="email-address" className="sr-only">
                    {t("footer.emailAddress")}
                  </label>
                  <input
                    type="email"
                    name="email-address"
                    id="email-address"
                    autoComplete="email"
                    required
                    className="w-full min-w-0 rounded-md border-0 bg-white/5 px-4 py-3 text-base text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-amber-500 sm:text-base sm:leading-6"
                    placeholder={t("footer.enterEmail")}
                  />
                  <button
                    type="submit"
                    className="flex-shrink-0 rounded-md bg-amber-500 px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-amber-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
                  >
                    {t("footer.subscribe")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
            {/* Bottom bar */}
            <div className="mt-16 border-t border-white/10 pt-8 sm:mt-20 lg:mt-24 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs leading-5 text-gray-400">
                &copy; {new Date().getFullYear()} MexaBeer. {t("footer.rights")}.
            </p>
            <a
                href="https://foeira.com"
                target="_blank"
                rel="noopener noreferrer"
                className=" font-mono text-xs leading-5 text-gray-600 hover:text-gray-200 transition-colors"
            >
                by Foeira
            </a>
            </div>
      </div>
    </footer>
  );
}